import axios, { AxiosRequestConfig, Method } from "axios";
import { captureException } from "./sentry";

import { store as advisorStore } from "@modules/advisor/state";
import eventEmitter from "@mobile/modules/common/eventEmitter";

type Data<T = null> = Record<string, string | boolean | number | null | T>;
type NestedData = Data<Data>;
type Headers = {
  [key: string]: string;
};

axios.interceptors.response.use(
  function (response) {
    return response;
  },
  async function (error) {
    const isReactNative = navigator.product === "ReactNative";
    const isWebSessionExpired =
      !isReactNative && error?.response?.status === 401;
    // An expired web session is handled by the redirect below, so it isn't
    // an error worth reporting (PORTAL-HT).
    if (!isWebSessionExpired) {
      captureException(error);
    }
    if (isReactNative) {
      eventEmitter.emit("checkForTokenExpired");
    } else if (isWebSessionExpired) {
      // Prevent downstream error handling while redirect occurs
      window.location.assign("/api/auth/login");
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return new Promise(() => {});
    }
    return Promise.reject(error);
  }
);

const request = async <T>(
  method: Method,
  url: string,
  data?: NestedData,
  headers?: Headers,
  skipImpersonation?: boolean
) => {
  const allHeaders = headers ?? {};
  const isReactNative = navigator.product === "ReactNative";
  if (isReactNative) {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const Auth0 = require("react-native-auth0").default;
    const auth0 = new Auth0({
      domain: `${process.env.AUTH0_BASE_URL_MOBILE}`,
      clientId: `${process.env.AUTH0_CLIENT_ID_MOBILE}`,
    });
    let credentials;
    try {
      credentials = await auth0.credentialsManager.getCredentials();
    } catch (error) {
      // Credentials that can't be renewed never reach the response
      // interceptor, so ask the listener to check (and sign out if needed).
      eventEmitter.emit("checkForTokenExpired");
      if ((error as { code?: string } | null)?.code === "NO_REFRESH_TOKEN") {
        // The session ended (ID token expired, no refresh token to renew it).
        // The listener signs the user out; skip the request rather than
        // surfacing an error for an expected expiry.
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        return new Promise<T>(() => {});
      }
      throw error;
    }
    allHeaders["Authorization"] = `Bearer ${credentials.idToken}`;
  }
  if (!skipImpersonation) {
    const { impersonateClientUserId } = advisorStore.getState();
    if (impersonateClientUserId) {
      allHeaders["x-forme-impersonate-client-user-id"] =
        impersonateClientUserId;
    }
  }

  let config: AxiosRequestConfig = {
    baseURL: process.env.PUBLIC_API_URL ?? "",
    headers: allHeaders,
    method,
    url,
  };

  if (data) {
    if (method === "GET") {
      config = {
        ...config,
        params: data,
      };
    } else {
      config = {
        ...config,
        data,
      };
    }
  }

  console.debug("API Request Config:", config);

  const res = await axios.request<T>(config);
  return res.data;
};

export default request;
